import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

const TabsCtx = createContext(null);
const KEY = "app_open_tabs_v1";

// Friendly default titles for known routes; dynamic routes are named by their page.
const STATIC_LABELS = {
  "/app": "Dashboard",
  "/lrs": "Lorry Receipts",
  "/consignments": "Consignments",
  "/field": "Field Mode",
  "/quotations": "Quotations",
  "/bids": "L&T Bids",
  "/invoices": "Invoices",
  "/aging": "Aging Report",
  "/expenses": "Expenses",
  "/bank-statements": "Bank Statements",
  "/payroll": "Payroll & Office",
  "/reports": "Reports",
  "/summary": "Summary",
  "/assistant": "AI Assistant",
  "/inbox": "Inbox Assistant",
  "/reminders": "Reminders",
  "/masters": "Vehicles & Drivers",
  "/users": "Users",
  "/settings": "Settings",
};

export function titleForPath(pathname) {
  if (STATIC_LABELS[pathname]) return STATIC_LABELS[pathname];
  if (pathname.startsWith("/lr/")) return "LR…";
  if (pathname.startsWith("/customer/")) return "Customer";
  if (pathname.startsWith("/entity/")) return "Entity";
  if (pathname.startsWith("/staff/")) return "Staff";
  if (pathname.startsWith("/handling/")) return "Handling";
  return pathname.replace(/^\//, "") || "Home";
}

export function TabsProvider({ children }) {
  const [tabs, setTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(tabs)); }, [tabs]);

  const openTab = useCallback((path, title) => {
    setTabs((prev) => {
      const i = prev.findIndex((t) => t.path === path);
      if (i >= 0) {
        if (title && prev[i].title !== title) {
          const next = [...prev]; next[i] = { ...next[i], title }; return next;
        }
        return prev;
      }
      return [...prev, { path, title: title || path }];
    });
  }, []);

  const closeTab = useCallback((path) => setTabs((prev) => prev.filter((t) => t.path !== path)), []);
  const closeOthers = useCallback((path) => setTabs((prev) => prev.filter((t) => t.path === path)), []);
  const closeAll = useCallback(() => setTabs([]), []);
  const renameTab = useCallback((path, title) => setTabs((prev) => {
    const i = prev.findIndex((t) => t.path === path);
    if (i < 0 || !title || prev[i].title === title) return prev;
    const next = [...prev]; next[i] = { ...next[i], title }; return next;
  }), []);

  return (
    <TabsCtx.Provider value={{ tabs, openTab, closeTab, closeOthers, closeAll, renameTab }}>
      {children}
    </TabsCtx.Provider>
  );
}

export const useTabs = () => useContext(TabsCtx);

// A page can call this to give its tab a meaningful title (e.g. the LR number).
export function useTabTitle(title) {
  const ctx = useTabs();
  const { pathname } = useLocation();
  useEffect(() => {
    if (title && ctx?.renameTab) ctx.renameTab(pathname, title);
  }, [title, pathname, ctx]);
}
